import { useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AuthContext } from 'components/context/AuthContext';
import { ModalsContext } from 'components/context/ModalsContext';
import { metaApi } from 'api';
import { toast } from 'react-hot-toast';
import {
  consumePendingMetaPermissionError,
  clearPendingMetaPermissionErrorStorage,
  isMetaAccountsPermissionRequiredError,
  metaPermissionErrorFromAccountsApi,
} from 'utils/metaPermissionErrors';

const META_ACTIVE_STALE_MS = 3 * 60 * 1000;

export const metaActiveAccountQueryKey = (accountId) => ['metaAds', 'activeAccount', accountId ?? ''];

async function loadMetaActiveAccountOrNull() {
  try {
    const res = await metaApi.fetchActiveMetaAccount();
    return res.data;
  } catch (e) {
    const status = e.response?.status;
    const errorMessage = e.response?.data?.error || e.message || '';
    const benignNoActive =
      status === 404 ||
      /no\s+hay\s+cuenta\s+activa/i.test(errorMessage) ||
      /no\s+active\s+account/i.test(errorMessage);
    if (!benignNoActive) {
      console.error('❌ useMetaAds: fetchActiveAccount error:', e);
      toast.error(`Error al cargar cuenta activa de Meta: ${errorMessage}`);
    }
    return null;
  }
}

export const useMetaAds = () => {
  const queryClient = useQueryClient();
  const { auth, updateContextUser, hasMetaConnected } = useContext(AuthContext);
  const { openConnectMetaAdsAccountModal } = useContext(ModalsContext);

  const openSelectMetaAccountModal = useCallback(() => {
    updateContextUser({ ...auth.user, meta_connected: true });
    openConnectMetaAdsAccountModal();
  }, [auth.user, updateContextUser, openConnectMetaAdsAccountModal]);

  const popupRef = useRef(null);

  const [isConnected, setIsConnected] = useState(!!auth.user?.meta_connected);
  const [loading, setLoading] = useState(false);
  const [loadingAccounts, setLoadingAcc] = useState(false);
  const [loadingCampaigns, setLoadingCampaigns] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [adminCampaigns, setAdminCampaigns] = useState([]);
  const [metaUserInfo, setMetaUserInfo] = useState(null);
  const [loadingUserInfo, setLoadingUserInfo] = useState(false);
  /** Inline Meta permission issue (403 META_PERMISSIONS_REQUIRED or OAuth callback) — do not toast. */
  const [metaAccountsPermissionError, setMetaAccountsPermissionError] = useState(null);
  const permissionErrorConsumedRef = useRef(false);

  const metaAdAccountId = auth?.user?.meta_ad_account_id ?? null;
  const metaActiveEnabled =
    Boolean(auth.user?.meta_connected) && Boolean(metaAdAccountId);
  const activeAccountQuery = useQuery({
    queryKey: metaActiveAccountQueryKey(metaAdAccountId),
    queryFn: loadMetaActiveAccountOrNull,
    enabled: metaActiveEnabled,
    staleTime: META_ACTIVE_STALE_MS,
    retry: false,
    refetchOnWindowFocus: false,
  });
  const activeAccount = metaActiveEnabled ? activeAccountQuery.data ?? null : null;
  const loadingActiveAccount = activeAccountQuery.isLoading;


  useEffect(() => {
    setIsConnected(!!auth.user?.meta_connected);
  }, [auth.user?.meta_connected]);

  const clearMetaAccountsPermissionError = useCallback(() => {
    setMetaAccountsPermissionError(null);
  }, []);

  // OAuth full-page redirect: read stashed permission error once on app load
  useEffect(() => {
    if (permissionErrorConsumedRef.current) return;
    permissionErrorConsumedRef.current = true;
    const pending = consumePendingMetaPermissionError();
    if (pending?.message) {
      setMetaAccountsPermissionError(pending);
    }
  }, []);

  const fetchMetaUserInfo = useCallback(async () => {
    if (!auth.user?.meta_connected) return;

    setLoadingUserInfo(true);
    try {
      const res = await metaApi.fetchMetaUserInfo();
      setMetaUserInfo(res.data);
      return res.data;
    } catch (e) {
      const errorMessage = e.response?.data?.error || e.message;
      setMetaUserInfo(null);
      toast.error(`Error al obtener info de usuario Meta: ${errorMessage}`);
    } finally {
      setLoadingUserInfo(false);
    }
  }, [auth.user?.meta_connected]);

  const fetchMetaAccounts = useCallback(async (force = false) => {
    if (!auth.user?.meta_connected) return;
    setLoadingAcc(true);
    try {
      const res = await metaApi.fetchMetaAccounts(force);
      setMetaAccountsPermissionError(null);
      setAccounts(res?.data?.accounts || []);
    } catch (e) {
      if (isMetaAccountsPermissionRequiredError(e)) {
        setMetaAccountsPermissionError(metaPermissionErrorFromAccountsApi(e));
        setAccounts([]);
      } else {
        const errorMessage = e.response?.data?.error || e.message;
        toast.error(`Error al cargar cuentas de Meta: ${errorMessage}`);
      }
    } finally {
      setLoadingAcc(false);
    }
  }, [auth.user?.meta_connected]);

  const startMetaOAuth = useCallback((force = false) => {
    if (auth.user?.meta_connected && !force) {
      if (popupRef?.current && !popupRef?.current?.closed) {
        popupRef.current.close();
      }
      openSelectMetaAccountModal();
      return;
    }

    const token = localStorage.getItem('access_token');
    if (!token) {
      alert('Token de sesión no disponible');
      return;
    }
    const url = `${process.env.REACT_APP_API_URL}/meta-auth/login?token=${token}`;
    const width = 500;
    const height = 700;
    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2;

    const popup = window.open(
      url,
      'MetaAdsConnectPopup',
      `width=${width},height=${height},left=${left},top=${top},menubar=no,toolbar=no,location=no,status=no,resizable=yes,scrollbars=yes`
    );
    popupRef.current = popup;

    // Poll for popup close
    const interval = setInterval(() => {
      if (!popup || popup.closed) {
        clearInterval(interval);
        window.removeEventListener('message', handleMessage);
        popupRef.current = null;
        return;
      }
    }, 3000);

    // Listen for messages from popup
    function handleMessage(event) {
      if (event.origin !== window.location.origin) return;
      const { data } = event;
      
      if (data.type === 'META_AUTH_SUCCESS') {
        clearPendingMetaPermissionErrorStorage();
        setMetaAccountsPermissionError(null);
        void fetchMetaAccounts(true);
        // Close the popup from parent
        if (popupRef.current && !popupRef.current.closed) {
          popupRef.current.close();
        }
        clearInterval(interval);
        openSelectMetaAccountModal();
        window.removeEventListener('message', handleMessage);
        popupRef.current = null;
      } else if (data.type === 'META_AUTH_PERMISSION_ERROR') {
        if (popupRef.current && !popupRef.current.closed) {
          popupRef.current.close();
        }
        clearInterval(interval);
        window.removeEventListener('message', handleMessage);
        popupRef.current = null;
        const payload = data.payload;
        if (payload?.message) {
          setMetaAccountsPermissionError({
            message: payload.message,
            missingPermissions: Array.isArray(payload.missingPermissions) ? payload.missingPermissions : [],
            source: payload.source || 'oauth_callback',
          });
        }
      } else if (data.type === 'META_AUTH_FAILURE') {
        // Close the popup from parent
        if (popupRef.current && !popupRef.current.closed) {
          popupRef.current.close();
        }
        clearInterval(interval);
        window.removeEventListener('message', handleMessage);
        popupRef.current = null;
        toast.error('Error al conectar Meta Ads. Por favor, inténtalo de nuevo.');
      }
    }
    window.addEventListener('message', handleMessage);
  }, [auth?.user?.meta_connected, openSelectMetaAccountModal, fetchMetaAccounts]);

  const updateMetaConnectionData = useCallback(async (payload) => {
    setLoading(true);
    try {
      const data = await metaApi.updateMetaConnectionData(payload);
      updateContextUser(data);
    } catch (e) {
      const errorMessage = e.response?.data?.error || e.message;
      toast.error(`Error al actualizar conexión de Meta: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  }, [updateContextUser]);

  const disconnect = useCallback(async () => {
    setLoading(true);
    try {
      const data = await metaApi.disconnectMetaAds();
      updateContextUser(data);
      setAccounts([]);
      setMetaAccountsPermissionError(null);
      setIsConnected(false);
      setMetaUserInfo(null);
      queryClient.removeQueries({ queryKey: ['metaAds', 'activeAccount'] });
    } catch (e) {
      const errorMessage = e.response?.data?.error || e.message;
      toast.error(`Error al desconectar cuenta Meta: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  }, [updateContextUser, queryClient]);

  const fetchCampaigns = useCallback(async (params) => {
    if (!hasMetaConnected) return;
    setLoadingCampaigns(true);
    try {
      const res = await metaApi.fetchMetaCampaigns(params);
      setCampaigns(res.data);
    } catch (e) {
      const errorMessage = e.response?.data?.error || e.message;
      toast.error(`Error al cargar campañas de Meta: ${errorMessage}`);
    } finally {
      setLoadingCampaigns(false);
    }
  }, [hasMetaConnected]);

  const fetchCampaignsForUser = useCallback(async (userId, params) => {
    if (!userId) return;
  
    setLoadingCampaigns(true);
    try {
      const res = await metaApi.fetchUserMetaCampaigns(userId, params);
      setAdminCampaigns(res.data);
    } catch (e) {
      const errorMessage = e.response?.data?.error || e.message;
      toast.error(`Error al cargar campañas de Meta del usuario: ${errorMessage}`);
    } finally {
      setLoadingCampaigns(false);
    }
  }, []);

  const fetchCampaignById = useCallback(async (campaignId) => {
    if (!campaignId) return;

    try {
      const res = await metaApi.fetchMetaCampaignById(campaignId);
      return res.data;
    } catch (e) {
      const errorMessage = e.response?.data?.error || e.message;
      toast.error(`Error al cargar campaña de Meta por ID: ${errorMessage}`);
    }
  }, []);

  const fetchActiveAccount = useCallback(async () => {
    if (!metaActiveEnabled) return undefined;
    return queryClient.fetchQuery({
      queryKey: metaActiveAccountQueryKey(metaAdAccountId),
      queryFn: loadMetaActiveAccountOrNull,
      staleTime: META_ACTIVE_STALE_MS,
    });
  }, [metaActiveEnabled, metaAdAccountId, queryClient]);

  const setActiveMetaAccount = useCallback(
    async (accountId, pageId) => {
      setLoading(true);
      try {
        const data = await metaApi.setActiveMetaAccount({ accountId, pageId });
        updateContextUser(data);
        await new Promise((resolve) => setTimeout(resolve, 500));
        await queryClient.invalidateQueries({ queryKey: ['metaAds', 'activeAccount'] });
        return data;
      } catch (e) {
        const errorMessage = e.response?.data?.error || e.message;
        console.error('❌ Error in setActiveMetaAccount:', e);
        toast.error(`Error al activar cuenta Meta: ${errorMessage}`);
      } finally {
        setLoading(false);
      }
    },
    [updateContextUser, queryClient]
  );

  return {
    isConnected,
    loading,
    loadingAccounts,
    accounts,
    metaAccountsPermissionError,
    clearMetaAccountsPermissionError,
    fetchMetaAccounts,
    disconnect,
    startMetaOAuth,
    loadingCampaigns,
    campaigns,
    fetchCampaigns,
    fetchCampaignsForUser,
    adminCampaigns,
    fetchCampaignById,
    activeAccount,
    fetchActiveAccount,
    loadingActiveAccount,
    fetchMetaUserInfo,     
    metaUserInfo,          
    loadingUserInfo,
    updateMetaConnectionData,
    setActiveMetaAccount
  };  
};
